import React, { useState, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  Database,
  Play,
  BarChart3,
  Settings,
  Zap,
  AlertCircle,
  FolderOpen,
  Eye,
  Plus,
  Tag,
  CheckSquare,
  BookOpen,
  Layers,
  Link2,
  Users,
  TrendingUp,
  Sliders,
  Package,
  Building2,
} from 'lucide-react';
import './App.css';

// Components
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import Endpoints from './components/Endpoints';
import Versions from './components/Versions';
import TestCases from './components/TestCases';
import TestTypes from './components/TestTypes';
import TestTechniques from './components/TestTechniques';
import TestCaseGeneration from './components/TestCaseGeneration';
import TestExecution from './components/TestExecution';
import Results from './components/Results';
import Evaluations from './components/Evaluations';
import Resource from './components/Resource';
import Assets from './components/Assets';
import Glossary from './components/Glossary';
import Documentation from './components/Documentation';
import Context from './components/Context';
import Domains from './components/Domains';
import EvaluationMappingsOverview from './components/EvaluationMappingsOverview';
import EvaluationFrameworks from './components/EvaluationFrameworks';
import Organizations from './components/Organizations';
import SignalMappings from './components/SignalMappings';
import AdminUsers from './components/AdminUsers';
import ConfigAdmin from './components/ConfigAdmin';
import Login from './components/Login';
import { SettingsProvider } from './hooks/useSettings';
import { RequireAuth, RequireAdmin } from './auth/guards';
import { useAuth } from './auth/AuthContext';

// Types
import { Notification } from './types';

function App() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const user = undefined; // placeholder to preserve future header usage if needed

  // Use ref to store stable notification functions - prevents remounting when notifications state changes
  const notificationRef = useRef<{
    add: (message: string, type: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
  }>({
    add: () => {}
  });
  const notificationIdSeq = useRef(0);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Actual notification function that updates state
  const addNotification = useCallback((message: string, type: 'success' | 'error' | 'info' | 'warning', duration: number = 5000) => {
    const id = `${Date.now()}-${++notificationIdSeq.current}`;
    const notification: Notification = { id, message, type, duration };
    
    setNotifications(prev => [...prev, notification]);
    
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  }, [removeNotification]);

  // Update ref with latest function - this allows us to always call the current implementation
  notificationRef.current.add = addNotification;

  // Create a completely stable wrapper function that never changes
  // This function reference will NEVER change, preventing component remounts
  const stableAddNotification = useCallback((message: string, type: 'success' | 'error' | 'info' | 'warning', duration?: number) => {
    notificationRef.current.add(message, type, duration);
  }, []); // Empty deps - this function reference NEVER changes

  // Memoize the TestCaseGeneration route element with empty dependency array
  // This ensures the element is created ONLY ONCE and never recreated
  // stableAddNotification never changes, and RequireAuth is a stable import
  const testCaseGenerationElement = useMemo(() => (
    <RequireAuth>
      <TestCaseGeneration addNotification={stableAddNotification} />
    </RequireAuth>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []); // Empty deps - element created once, never recreated

  // Memoize navigationItems to prevent AppContent re-renders
  // This array is constant and doesn't change, so memoizing it ensures stable reference
  const navigationItems = useMemo(() => [
    {
      name: 'Dashboard',
      icon: BarChart3,
      path: '/',
      description: 'Overview and statistics'
    },
    {
      name: 'Test Generation',
      icon: FolderOpen,
      description: 'Manage and generate test cases',
      children: [
        {
          name: 'View Test Cases',
          icon: Eye,
          path: '/test-cases',
          description: 'View and manage existing test cases'
        },
        {
          name: 'Test Case Generation',
          icon: Plus,
          path: '/test-case-generation',
          description: 'Generate new test cases from various sources'
        }
      ]
    },
    {
      name: 'Test Mapping',
      icon: Layers,
      description: 'Hierarchical test organization and evaluation configuration',
      children: [
        {
          name: 'Domains',
          icon: Database,
          path: '/context/domains',
          description: 'Manage top-level test domains (Knowledge Base, RAG, Agent)'
        },
        {
          name: 'Categories',
          icon: Tag,
          path: '/context/test-types',
          description: 'Manage test categories within domains'
        },
        {
          name: 'Techniques',
          icon: Settings,
          path: '/context/test-techniques',
          description: 'Manage test techniques within categories'
        },
        {
          name: 'Evaluation Mappings',
          icon: Link2,
          path: '/context/evaluation-mappings',
          description: 'Map techniques to evaluation metrics'
        }
      ]
    },
    {
      name: 'Test Runs',
      icon: BarChart3,
      description: 'Execute tests and view evaluation results',
      children: [
        {
          name: 'Summary',
          icon: Zap,
          path: '/results',
          description: 'View test results and evaluations'
        },
        {
          name: 'Test Case Execution',
          icon: Play,
          path: '/test-execution',
          description: 'Run tests and evaluations'
        },
        {
          name: 'AUT Response Evaluation',
          icon: CheckSquare,
          path: '/evaluations',
          description: 'Upload Excel files and run RAGAS response evaluations'
        }
      ]
    },
    {
      name: 'Assets',
      icon: Package,
      description: 'Manage and organize testing assets',
      children: [
        {
          name: 'Evaluation Frameworks',
          icon: Layers,
          path: '/assets/evaluation-frameworks',
          description: 'Manage evaluation frameworks (RAGAS, OpenAI, etc.)'
        },
        {
          name: 'Signal Mappings',
          icon: TrendingUp,
          path: '/assets/signal-mappings',
          description: 'Configure intelligent test category recommendations'
        }
      ]
    },
    {
      name: 'Resource',
      icon: BookOpen,
      description: 'Educational resources and documentation',
      children: [
        {
          name: 'Glossary',
          icon: BookOpen,
          path: '/resource/glossary',
          description: 'Definitions of LLM evaluation metrics'
        },
        {
          name: 'Documentation',
          icon: BookOpen,
          path: '/resource/documentation',
          description: 'Technical documentation and implementation guides'
        }
      ]
    },
    {
      name: 'AUT',
      icon: Settings,
      description: 'Application configuration and management',
      children: [
        {
          name: 'Endpoints',
          icon: Settings,
          path: '/endpoints',
          description: 'Manage endpoints'
        },
        {
          name: 'Versions',
          icon: Database,
          path: '/versions',
          description: 'Manage versions'
        },
      ]
    }
  ], []); // Empty deps - navigationItems never changes

  // Settings section at bottom of sidebar (admin only): orgs, users, combined admin console
  const configBottomItems = useMemo(() => [
    {
      name: 'Settings',
      icon: Sliders,
      description: 'Settings and administration',
      children: [
        {
          name: 'Organizations',
          icon: Building2,
          path: '/organizations',
          description: 'Create organizations and projects',
        },
        {
          name: 'Admin users',
          icon: Users,
          path: '/admin/users',
          description: 'Manage users and project access',
        },
        {
          name: 'Projects',
          icon: Settings,
          path: '/config/admin',
          description: 'Organizations, projects, and users'
        }
      ]
    }
  ], []);

  return (
    <SettingsProvider>
      <Router>
        {/* Notifications UI moved to App level so it renders independently of AppContent */}
        {/* This allows notifications to display even when AppContent is memoized */}
        <NotificationContainer 
          notifications={notifications}
          removeNotification={removeNotification}
        />
        
        <AppContent 
          navigationItems={navigationItems}
          configBottomItems={configBottomItems}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          testCaseGenerationElement={testCaseGenerationElement}
          stableAddNotification={stableAddNotification}
          removeNotification={removeNotification}
        />
      </Router>
    </SettingsProvider>
  );
}

// Notification container component that checks location to conditionally show notifications
const NotificationContainer: React.FC<{ notifications: Notification[]; removeNotification: (id: string) => void }> = ({ notifications, removeNotification }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  // Don't show notifications on login page
  if (isLoginPage) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm sm:max-w-md md:max-w-lg pointer-events-none">
      {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`w-full bg-slate-800 shadow-xl rounded-lg border border-slate-700 pointer-events-auto ring-1 ring-slate-600 ring-opacity-50 overflow-hidden transform transition-all duration-300 ease-in-out animate-in slide-in-from-right-5 ${
                notification.type === 'success' ? 'ring-green-500 border-l-4 border-green-500' :
                notification.type === 'error' ? 'ring-red-500 border-l-4 border-red-500' :
                notification.type === 'warning' ? 'ring-yellow-500 border-l-4 border-yellow-500' :
                'ring-blue-500 border-l-4 border-blue-500'
              }`}
            >
              <div className="p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {notification.type === 'success' && <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>}
                    {notification.type === 'error' && <AlertCircle className="w-6 h-6 text-red-400" />}
                    {notification.type === 'warning' && <AlertCircle className="w-6 h-6 text-yellow-400" />}
                    {notification.type === 'info' && <AlertCircle className="w-6 h-6 text-blue-400" />}
                  </div>
                  <div className="ml-3 flex-1 pt-0.5">
                    <p className="text-sm font-medium text-slate-100 break-words">
                      {notification.message}
                    </p>
                  </div>
                  <div className="ml-4 flex-shrink-0 flex">
                    <button
                      className="bg-slate-700 rounded-md inline-flex text-slate-300 hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 p-1"
                      onClick={() => removeNotification(notification.id)}
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
  );
};

// Move AppContent outside App to prevent recreation on every render
// This is CRITICAL - if AppContent is defined inside App, it's recreated on every render
// causing React Router to remount all routes
interface AppContentProps {
  navigationItems: any[];
  configBottomItems: any[];
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  testCaseGenerationElement: React.ReactElement;
  stableAddNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
  removeNotification: (id: string) => void;
}

// Memoize AppContent to prevent re-renders when notifications change
// This prevents React Router from remounting routes unnecessarily
// Using default shallow comparison since notifications prop was removed (now rendered in App)
const AppContent: React.FC<AppContentProps> = React.memo(({
  navigationItems,
  configBottomItems,
  sidebarCollapsed,
  setSidebarCollapsed,
  testCaseGenerationElement,
  stableAddNotification,
  removeNotification
}) => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const { roles } = useAuth();
  const isAdmin = roles.some((r) => r.toUpperCase() === 'ADMIN');

  const filteredBottomItems = React.useMemo(() => {
    if (!isAdmin) return [];
    return configBottomItems;
  }, [configBottomItems, isAdmin]);

  if (isLoginPage) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  }

  return (
    <div className="flex h-screen bg-slate-900">
      {/* Sidebar */}
      <Sidebar 
        items={navigationItems as any[]}
        bottomItems={filteredBottomItems as any[]}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header 
          onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          sidebarCollapsed={sidebarCollapsed}
        />

        {/* Main Content Area */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-slate-900 p-6 text-slate-100">
          <Routes>

              <Route
                path="/"
                element={
                  <RequireAuth>
                    <Dashboard addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/endpoints"
                element={
                  <RequireAdmin>
                    <Endpoints addNotification={stableAddNotification} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/versions"
                element={
                  <RequireAdmin>
                    <Versions addNotification={stableAddNotification} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/test-cases"
                element={
                  <RequireAuth>
                    <TestCases addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/test-case-generation"
                element={testCaseGenerationElement}
              />
              <Route
                path="/test-execution"
                element={
                  <RequireAuth>
                    <TestExecution addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/context"
                element={
                  <RequireAuth>
                    <Context addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/context/domains"
                element={
                  <RequireAuth>
                    <Domains addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/context/test-types"
                element={
                  <RequireAuth>
                    <TestTypes addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/context/test-techniques"
                element={
                  <RequireAuth>
                    <TestTechniques addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/context/evaluation-mappings"
                element={
                  <RequireAuth>
                    <EvaluationMappingsOverview addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/assets/evaluation-frameworks"
                element={
                  <RequireAuth>
                    <EvaluationFrameworks addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/context/evaluation-frameworks"
                element={<Navigate to="/assets/evaluation-frameworks" replace />}
              />
              <Route
                path="/assets/signal-mappings"
                element={
                  <RequireAuth>
                    <SignalMappings addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/context/signal-mappings"
                element={<Navigate to="/assets/signal-mappings" replace />}
              />
              <Route
                path="/results"
                element={
                  <RequireAuth>
                    <Results addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/evaluations"
                element={
                  <RequireAuth>
                    <Evaluations addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/assets"
                element={
                  <RequireAuth>
                    <Assets addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/resource"
                element={
                  <RequireAuth>
                    <Resource addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/resource/glossary"
                element={
                  <RequireAuth>
                    <Glossary addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/resource/documentation"
                element={
                  <RequireAuth>
                    <Documentation addNotification={stableAddNotification} />
                  </RequireAuth>
                }
              />
              <Route
                path="/organizations"
                element={
                  <RequireAdmin>
                    <Organizations addNotification={stableAddNotification} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <RequireAdmin>
                    <AdminUsers addNotification={stableAddNotification} />
                  </RequireAdmin>
                }
              />
              <Route
                path="/config/admin"
                element={
                  <RequireAdmin>
                    <ConfigAdmin addNotification={stableAddNotification} />
                  </RequireAdmin>
                }
              />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
          </div>
        </div>
    );
  }); // Default shallow comparison - all props are stable (notifications removed, functions memoized)

export default App;
